import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Control, useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { OrderDetails, OrderItem, ProductOrderItem } from 'types/api/orders.type';
import { PriceType } from 'types/api/pricing.type';

import { priceTypeStore } from 'lib/priceTypeStore';

import {
  ordersApi,
  useDeleteItemMutation,
  useUpdateItemsSizesMutation,
} from 'redux/api/ordersApi';
import { useAppDispatch } from 'redux/hooks';

import { ordersDataService } from 'services';

import { OrderContext } from 'contexts/OrderContext';

import { downloadFile } from 'helpers/downloadFile';
import { getApiErrorMessage } from 'helpers/getApiErrorMessage';
import {
  getDefaultPriceType,
  getInitialPriceType,
  getOrderAvailablePriceTypes,
} from 'helpers/pricing';

import { useOrder } from 'pages/Orders/components/Order/Order.hooks';

import { StyleGroup, groupByStyle } from './utils/groupByStyle';

type UseProductTableReturn = {
  styleGroups: StyleGroup[];
  orderTotal: { quantity: number; price: number; currency: string };
  isSend: boolean;
  onQuantityBlur: (
    item: ProductOrderItem,
    size: string,
    quantity: number,
    inputName?: string
  ) => Promise<void>;
  onDeliveryCodeChange: (styleKey: string, deliveryCode: string) => Promise<void>;
  onDeleteColour: (item: ProductOrderItem) => Promise<void>;
  onDeleteStyle: (styleKey: string) => Promise<void>;
  isDownloading: boolean;
  isUploading: boolean;
  isTemplateModalVisible: boolean;
  onOpenTemplateModal: () => void;
  onCloseTemplateModal: () => void;
  onDownloadTemplateWithParams: (params: {
    gender?: string;
    category?: string;
  }) => Promise<void>;
  onUploadProducts: () => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  onFileSelected: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  control: Control;
  selectedPriceType: PriceType | undefined;
  selectedPriceTypeSet: (type: PriceType) => void;
  availablePriceTypes: PriceType[];
};

/** Edited cells of one item waiting to be saved, by size. */
type PendingItemSave = { item: ProductOrderItem; sizes: Map<string, number> };

/** Most items sent in one batch save (the backend accepts up to 100). */
const SAVE_BATCH_MAX_ITEMS = 100;

/** Pause before the single automatic retry of a save that got no usable response. */
const SAVE_RETRY_DELAY_MS = 1000;

/**
 * A save worth retrying once: no usable response reached the app (RTK Query's FETCH_ERROR /
 * TIMEOUT_ERROR — what the browser reports as "TypeError: Failed to fetch", e.g. a gateway
 * 502/504 without CORS headers) or a gateway status that did reach it. Validation and business
 * errors (4xx) are never retried. Repeating is safe: the PATCH carries absolute quantities.
 */
const isRetryableSaveError = (err: unknown): boolean => {
  const status = (err as { status?: unknown } | null)?.status;
  return (
    status === 'FETCH_ERROR' ||
    status === 'TIMEOUT_ERROR' ||
    status === 502 ||
    status === 503 ||
    status === 504
  );
};

const withOneRetry = async <T>(send: () => Promise<T>): Promise<T> => {
  try {
    return await send();
  } catch (err: unknown) {
    if (!isRetryableSaveError(err)) throw err;
    await new Promise((resolve) => {
      setTimeout(resolve, SAVE_RETRY_DELAY_MS);
    });
    return send();
  }
};

const useProductTable = (): UseProductTableReturn => {
  const { control, setValue, setError, clearErrors } = useForm();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { basicInfo, order, setOrder, isSend, orderHeader } = useOrder();
  const { setOrderTotal, setOrderPriceType, registerQuantitySaveWait } =
    useContext(OrderContext);

  const [isDownloading, setIsDownloading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [updateItemsSizes] = useUpdateItemsSizesMutation();
  const [deleteItemMutation] = useDeleteItemMutation();

  const items = order?.items ?? [];
  const orderId = order?.id ?? basicInfo?.orderNumber ?? '';

  // Latest order, so saves that resolve one after another build on each
  // other instead of on the order captured by the render that queued them.
  const orderRef = useRef(order);
  orderRef.current = order;

  // Groups of untouched styles keep their identity across item updates, so only
  // the edited style's row re-renders (see groupByStyle).
  const styleGroupsRef = useRef<StyleGroup[]>([]);
  const styleGroups = useMemo(() => {
    styleGroupsRef.current = groupByStyle(items as ProductOrderItem[], styleGroupsRef.current);
    return styleGroupsRef.current;
  }, [items]);

  // --- Price type selection ---
  const availablePriceTypes = useMemo(() => getOrderAvailablePriceTypes(items), [items]);

  const [selectedPriceType, selectedPriceTypeSet] = useState<PriceType | undefined>();

  // Auto-select the remembered (or default) price type when items load or change
  useEffect(() => {
    if (items.length > 0 && !selectedPriceType) {
      const firstSizeWithPrices = items
        .flatMap((item) => item.sizes ?? [])
        .find((s) => s.resolvedPrices && s.resolvedPrices.length > 0);
      const defaultType = getDefaultPriceType(firstSizeWithPrices?.resolvedPrices);
      const initialType = getInitialPriceType(
        priceTypeStore.getPriceType(),
        availablePriceTypes,
        defaultType
      );
      if (initialType) selectedPriceTypeSet(initialType);
    }
  }, [items]);

  const orderTotal = useMemo(() => {
    const quantity = order?.totalQuantity ?? 0;
    const priceType = selectedPriceType ?? 'WHOLESALE';
    const resolved = order?.resolvedTotals?.[priceType];

    return {
      quantity,
      price: resolved?.price ?? 0,
      currency: resolved?.currency ?? '',
    };
  }, [order?.totalQuantity, order?.resolvedTotals, selectedPriceType]);

  // Sync orderTotal to context so OrderHeader can read it
  useEffect(() => {
    setOrderTotal(orderTotal);
  }, [orderTotal, setOrderTotal]);

  // Same for the price type, so the order charts value the order like the total
  useEffect(() => {
    setOrderPriceType(selectedPriceType);
  }, [selectedPriceType, setOrderPriceType]);

  const applyItemsMutation = useCallback(
    (
      savedItems: Array<{ target: ProductOrderItem; item: OrderItem }>,
      totals: {
        orderTotalQuantity: number;
        orderTotalPrice: number;
        resolvedTotals?: OrderDetails['resolvedTotals'];
      }
    ) => {
      const { current } = orderRef;
      if (!current) return;
      const byId = new Map(savedItems.map((s) => [s.target.itemId, s]));
      const nextItems = current.items.map((i) => {
        const saved = byId.get(i.itemId);
        return saved ? { ...i, ...saved.item, uniqueId: saved.target.uniqueId } : i;
      });
      const next = {
        ...current,
        items: nextItems,
        totalQuantity: totals.orderTotalQuantity,
        totalPrice: totals.orderTotalPrice,
        resolvedTotals: totals.resolvedTotals ?? current.resolvedTotals,
      };
      orderRef.current = next;
      setOrder(next);
    },
    [setOrder]
  );

  const removeItems = useCallback(
    (
      itemIds: string[],
      totals: {
        quantity: number;
        price: number;
        resolvedTotals?: OrderDetails['resolvedTotals'];
      }
    ) => {
      if (!order) return;
      setOrder({
        ...order,
        items: order.items.filter((i) => !itemIds.includes(i.itemId)),
        totalQuantity: totals.quantity,
        totalPrice: totals.price,
        resolvedTotals: totals.resolvedTotals ?? order.resolvedTotals,
      });
    },
    [order, setOrder]
  );

  // Edited cells are saved through one background queue: one batch PATCH
  // carrying every pending size of every edited item, one request at a time,
  // and a single "saving" toast for the whole run. Cells edited while a request
  // is in flight go into the next batch. The backend saves each item of a
  // batch on its own and computes the order totals once — on a large order a
  // request per item spent most of its time recomputing those totals.
  const pendingSavesRef = useRef(new Map<string, PendingItemSave>());
  // Last value queued per field (`${uniqueId}-${size}`): what the server will
  // hold once the queue drains, so a blur that does not change it is skipped.
  const queuedValuesRef = useRef(new Map<string, number>());
  const isSavingRef = useRef(false);
  const saveToastIdRef = useRef<string>();
  const saveErrorRef = useRef<string>();
  // Cells whose save failed and were reverted: shown in red (RHF field error) until the user
  // edits them again or they save; while any is left, the order can't be sent.
  const failedFieldsRef = useRef(new Set<string>());
  // Callers waiting for the queue to go idle (`waitForQuantitySaves`).
  const idleWaitersRef = useRef<Array<() => void>>([]);

  const markNotSaved = useCallback(
    (fieldName: string) => {
      failedFieldsRef.current.add(fieldName);
      setError(fieldName, { type: 'save', message: t('orderTable.notSaved') });
    },
    [setError, t]
  );

  const clearNotSaved = useCallback(
    (fieldName: string) => {
      if (!failedFieldsRef.current.delete(fieldName)) return;
      clearErrors(fieldName);
    },
    [clearErrors]
  );

  const savedQuantity = (item: ProductOrderItem, size: string): number => {
    const latest =
      (orderRef.current?.items.find((i) => i.itemId === item.itemId) as
        | ProductOrderItem
        | undefined) ?? item;
    return latest.sizes?.find((s) => s.size === size)?.quantity ?? 0;
  };

  const drainSaveQueue = useCallback(async (): Promise<void> => {
    if (isSavingRef.current) return;
    isSavingRef.current = true;

    // Resets cells whose save was rejected to the last saved value so they don't
    // show a quantity as if it were confirmed — unless a newer edit of the cell
    // is already queued, which will be saved on its own.
    const revertFailed = (pending: PendingItemSave, sizes: Array<{ size: string }>) => {
      const queuedAgain = pendingSavesRef.current.get(pending.item.itemId)?.sizes;
      for (const { size } of sizes) {
        if (!queuedAgain?.has(size)) {
          const fieldName = `${pending.item.uniqueId}-${size}`;
          const saved = savedQuantity(pending.item, size);
          queuedValuesRef.current.delete(fieldName);
          setValue(fieldName, saved === 0 ? '' : saved);
          markNotSaved(fieldName);
        }
      }
    };

    try {
      while (pendingSavesRef.current.size > 0) {
        const batch = Array.from(pendingSavesRef.current.values())
          .slice(0, SAVE_BATCH_MAX_ITEMS)
          .map((pending) => ({
            pending,
            sizes: Array.from(pending.sizes, ([size, quantity]) => ({ size, quantity })),
          }));
        batch.forEach(({ pending }) => pendingSavesRef.current.delete(pending.item.itemId));
        const batchItems = batch.map(({ pending, sizes }) => ({
          itemId: pending.item.itemId,
          sizes,
        }));

        try {
          // eslint-disable-next-line no-await-in-loop
          const res = await withOneRetry(() =>
            updateItemsSizes({ orderId, items: batchItems }).unwrap()
          );
          const resultById = new Map(res.results.map((r) => [r.itemId, r]));
          const savedItems: Array<{ target: ProductOrderItem; item: OrderItem }> = [];

          for (const { pending, sizes } of batch) {
            const result = resultById.get(pending.item.itemId);
            if (result?.item) {
              savedItems.push({ target: pending.item, item: result.item });
              // The order now holds these values; drop them unless edited again.
              for (const { size, quantity } of sizes) {
                const fieldName = `${pending.item.uniqueId}-${size}`;
                if (queuedValuesRef.current.get(fieldName) === quantity) {
                  queuedValuesRef.current.delete(fieldName);
                }
                clearNotSaved(fieldName);
              }
            }
          }
          // Saved items first, so cells reverted below read their saved values from the
          // updated order.
          applyItemsMutation(savedItems, res);

          for (const { pending, sizes } of batch) {
            const result = resultById.get(pending.item.itemId);
            if (!result?.item) {
              saveErrorRef.current = result?.error?.message || t('orderTable.errorSavingQty');
              revertFailed(pending, sizes);
            }
          }
        } catch (err: unknown) {
          saveErrorRef.current = getApiErrorMessage(err, t('orderTable.errorSavingQty'));
          batch.forEach(({ pending, sizes }) => revertFailed(pending, sizes));
        }
      }
    } finally {
      isSavingRef.current = false;
      const toastId = saveToastIdRef.current;
      if (toastId) {
        if (saveErrorRef.current) toast.error(saveErrorRef.current, { id: toastId });
        else toast.success(t('orderTable.savedQty'), { id: toastId });
      }
      saveToastIdRef.current = undefined;
      saveErrorRef.current = undefined;
      idleWaitersRef.current.splice(0).forEach((notify) => notify());
    }
  }, [
    orderId,
    t,
    updateItemsSizes,
    applyItemsMutation,
    setValue,
    markNotSaved,
    clearNotSaved,
  ]);

  const onQuantityBlur = useCallback(
    async (item: ProductOrderItem, size: string, quantity: number): Promise<void> => {
      if (isSend || !orderId || !basicInfo) return;

      // Blur also fires when the user just tabs through a cell.
      const fieldName = `${item.uniqueId}-${size}`;
      // Touching a red cell again acknowledges it: a new value is saved below, keeping the
      // reverted one is a deliberate choice.
      clearNotSaved(fieldName);
      const expected = queuedValuesRef.current.get(fieldName) ?? savedQuantity(item, size);
      if (quantity === expected) return;

      queuedValuesRef.current.set(fieldName, quantity);
      const pending = pendingSavesRef.current.get(item.itemId) ?? { item, sizes: new Map() };
      pending.sizes.set(size, quantity);
      pendingSavesRef.current.set(item.itemId, pending);

      if (!saveToastIdRef.current) {
        saveToastIdRef.current = toast.loading(t('orderTable.savingQty'));
      }
      await drainSaveQueue();
    },
    [isSend, orderId, basicInfo, t, drainSaveQueue, clearNotSaved]
  );

  /** Resolves once the save queue is idle: `true` when no quantity is left unsaved. */
  const waitForQuantitySaves = useCallback((): Promise<boolean> => {
    const allSaved = () => failedFieldsRef.current.size === 0;
    if (!isSavingRef.current && pendingSavesRef.current.size === 0) {
      return Promise.resolve(allSaved());
    }
    return new Promise((resolve) => {
      idleWaitersRef.current.push(() => resolve(allSaved()));
    });
  }, []);

  useEffect(() => {
    registerQuantitySaveWait(waitForQuantitySaves);
    return () => registerQuantitySaveWait(null);
  }, [registerQuantitySaveWait, waitForQuantitySaves]);

  // Leaving or reloading the page while a quantity is still being saved would lose it.
  useEffect(() => {
    const warnIfSaving = (event: BeforeUnloadEvent) => {
      if (!isSavingRef.current && pendingSavesRef.current.size === 0) return;
      event.preventDefault();
      // eslint-disable-next-line no-param-reassign
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnIfSaving);
    return () => window.removeEventListener('beforeunload', warnIfSaving);
  }, []);

  const onDeliveryCodeChange = useCallback(
    async (_styleKey: string, _deliveryCode: string): Promise<void> => {},
    []
  );

  const onDeleteColour = useCallback(
    async (item: ProductOrderItem): Promise<void> => {
      if (isSend || !orderId || !basicInfo) return;

      try {
        const res = await deleteItemMutation({ orderId, itemId: item.itemId }).unwrap();
        removeItems([item.itemId], {
          quantity: res.orderTotalQuantity,
          price: res.orderTotalPrice,
          resolvedTotals: res.resolvedTotals,
        });
      } catch {
        toast.error(t('error.deletingColour'));
      }
    },
    [isSend, basicInfo, orderId, t, deleteItemMutation, removeItems]
  );

  const onDeleteStyle = useCallback(
    async (styleKey: string): Promise<void> => {
      if (isSend || !orderId || !basicInfo) return;

      const group = styleGroups.find((g) => g.styleKey === styleKey);
      if (!group) return;

      const deletedItemIds: string[] = [];
      let anyFailed = false;
      let latestTotals = {
        quantity: 0,
        price: 0,
        resolvedTotals: undefined as OrderDetails['resolvedTotals'],
      };

      for (const colour of group.colours) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const res = await deleteItemMutation({
            orderId,
            itemId: colour.itemId,
          }).unwrap();
          deletedItemIds.push(colour.itemId);
          latestTotals = {
            quantity: res.orderTotalQuantity,
            price: res.orderTotalPrice,
            resolvedTotals: res.resolvedTotals,
          };
        } catch {
          anyFailed = true;
        }
      }

      if (deletedItemIds.length > 0) {
        removeItems(deletedItemIds, latestTotals);
      }

      if (anyFailed) {
        toast.error(t('error.deletingStyle'));
      }
    },
    [isSend, basicInfo, orderId, styleGroups, t, deleteItemMutation, removeItems]
  );

  const [isTemplateModalVisible, setIsTemplateModalVisible] = useState(false);

  const onOpenTemplateModal = useCallback((): void => {
    setIsTemplateModalVisible(true);
  }, []);

  const onCloseTemplateModal = useCallback((): void => {
    setIsTemplateModalVisible(false);
  }, []);

  const onDownloadTemplateWithParams = useCallback(
    async (params: { gender?: string; category?: string }): Promise<void> => {
      if (!orderId) return;
      setIsDownloading(true);
      try {
        const res = await ordersDataService.getImportTemplate(orderId, params);

        const parts: string[] = ['Order'];
        if (orderHeader?.orderNumber) {
          parts.push(String(orderHeader.orderNumber));
        }
        if (params.gender) parts.push(params.gender);
        // A category can list several codes (comma-separated); the first names the file.
        if (params.category) parts.push(params.category.split(',')[0]);
        if (orderHeader?.zone) parts.push(orderHeader.zone);
        if (orderHeader?.season) parts.push(orderHeader.season);
        if (orderHeader?.collectionCode) parts.push(orderHeader.collectionCode);

        const filename = `${parts.join('_').replace(/\s+/g, '_')}.xlsx`;

        downloadFile({
          data: res.data,
          filename,
          mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        setIsTemplateModalVisible(false);
      } catch {
        toast.error(t('orderTable.errorDownloadTemplate'));
      } finally {
        setIsDownloading(false);
      }
    },
    [orderId, orderHeader, t]
  );

  const onUploadProducts = useCallback((): void => {
    fileInputRef.current?.click();
  }, []);

  const onFileSelected = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
      const file = e.target.files?.[0];
      if (!file || !orderId) {
        if (!orderId) toast.error(t('orderTable.errorUploadProducts'));
        return;
      }

      e.target.value = '';
      setIsUploading(true);
      try {
        const formData = new FormData();
        formData.append('file', file);

        await ordersDataService.importOrder(orderId, formData);
        toast.success(t('order.importedSuccessfully'));
        dispatch(ordersApi.util.invalidateTags([{ type: 'OrderHeader', id: orderId }]));
      } catch (err: unknown) {
        const error = err as {
          response?: { data?: { apierror?: { message?: string } } };
        };
        const message =
          error.response?.data?.apierror?.message || t('orderTable.errorUploadProducts');
        toast.error(message);
      } finally {
        setIsUploading(false);
      }
    },
    [orderId, dispatch, t]
  );

  return {
    styleGroups,
    orderTotal,
    isSend,
    onQuantityBlur,
    onDeliveryCodeChange,
    onDeleteColour,
    onDeleteStyle,
    isDownloading,
    isUploading,
    isTemplateModalVisible,
    onOpenTemplateModal,
    onCloseTemplateModal,
    onDownloadTemplateWithParams,
    onUploadProducts,
    fileInputRef,
    onFileSelected,
    control,
    selectedPriceType,
    selectedPriceTypeSet,
    availablePriceTypes,
  };
};

export { useProductTable };
